import React, { useRef, useEffect } from 'react';

interface OtpVerificationFormProps {
  mobileNumber: string;
  otp: string[];
  isLoading: boolean;
  error: string | null;
  onOtpChange: (index: number, value: string) => void;
  onKeyDown: (index: number, e: React.KeyboardEvent) => void;
  onSubmit: (e: React.FormEvent) => void;
  onResend: () => void;
  onEditMobile: () => void;
}

const OtpVerificationForm: React.FC<OtpVerificationFormProps> = ({
  mobileNumber,
  otp,
  isLoading,
  error,
  onOtpChange,
  onKeyDown,
  onSubmit,
  onResend,
  onEditMobile,
}) => {
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (otpRefs.current[0]) {
      otpRefs.current[0].focus();
    }
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    onOtpChange(index, value);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    onKeyDown(index, e);
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <label className="text-gray-900 text-sm font-semibold">
            Enter 6-digit OTP
          </label>
          <button
            type="button"
            onClick={onEditMobile}
            className="text-orange-500 text-sm font-medium hover:underline"
          >
            Edit (+91 {mobileNumber})
          </button>
        </div>

        <div className="flex justify-center space-x-3 mb-4">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { otpRefs.current[index] = el; }}
              type="tel"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-12 text-center text-xl font-bold bg-gray-50 border-2 border-gray-300 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 focus:bg-white"
              maxLength={1}
            />
          ))}
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={onResend}
            disabled={isLoading}
            className="text-orange-500 text-sm font-medium hover:underline disabled:opacity-50"
          >
            Didn't receive OTP? Resend
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading || otp.join('').length !== 6}
        className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3 rounded-md font-semibold transition-colors"
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Verifying...
          </div>
        ) : (
          'Verify & Login'
        )}
      </button>
    </form>
  );
};

export default OtpVerificationForm;
